import React from 'react';
import { ActionIcon, Group, Menu } from '@mantine/core';
import { IconDotsVertical, IconEdit, IconEye } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PropagationStopper, When } from '../../components/common';

type SurveyActionButtonProps = {
    surveyId: string;
    showEditIcon: boolean;
    onEyeClick?: (surveyId: string) => void;
    onCancelSurvey?: (surveyId: string) => void;
};

export function SurveyActionButton({
    surveyId,
    showEditIcon,
    onEyeClick,
    onCancelSurvey,
}: SurveyActionButtonProps) {
    const navigate = useNavigate();
    const showCancelAction = Boolean(onCancelSurvey);

    let labelAndTitle: string;
    if (showEditIcon) {
        labelAndTitle = 'Edit survey';
    } else if (onEyeClick) {
        labelAndTitle = 'View survey history';
    } else {
        labelAndTitle = 'View survey details';
    }

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!showEditIcon && onEyeClick) {
            onEyeClick(surveyId);
        } else {
            navigate(`/price-surveys/survey/${surveyId}`);
        }
    };

    const handleCancelSurvey = () => {
        onCancelSurvey?.(surveyId);
    };

    return (
        <PropagationStopper>
            <Group gap={4} wrap="nowrap" justify="center">
                <ActionIcon
                    variant="light"
                    color="primary"
                    size="md"
                    aria-label={labelAndTitle}
                    title={labelAndTitle}
                    className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer"
                    onClick={handleClick}
                >
                    {showEditIcon ? (
                        <IconEdit size={16} />
                    ) : (
                        <IconEye size={16} />
                    )}
                </ActionIcon>
                <When condition={showCancelAction}>
                    <Menu
                        shadow="md"
                        width={200}
                        position="bottom-end"
                        withinPortal
                    >
                        <Menu.Target>
                            <ActionIcon
                                variant="subtle"
                                color="primary"
                                size="md"
                                aria-label="More survey actions"
                                className="w-8 h-8 rounded-md flex items-center justify-center cursor-pointer text-primary hover:bg-primary/10 hover:text-primary"
                            >
                                <IconDotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item color="red" onClick={handleCancelSurvey}>
                                Cancel Survey
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </When>
            </Group>
        </PropagationStopper>
    );
}
